import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import SharePanel from "@/components/landing/SharePanel";

const centsToEuro = (c) =>
  ((c || 0) / 100).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const UserAreaPage = () => {
  const { user, loading: authLoading, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [network, setNetwork] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dashboard"); // dashboard | profile

  // Profile editing state
  const [profileForm, setProfileForm] = useState({ nome: "", cognome: "", telefono: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return nav("/login");
    (async () => {
      try {
        const [n, e, p] = await Promise.all([
          api.get("/me/network"),
          api.get("/me/earnings"),
          api.get("/progress"),
        ]);
        setNetwork(n.data);
        setEarnings(e.data);
        setProgress(p.data);
        setProfileForm({
          nome: user.nome || "",
          cognome: user.cognome || "",
          telefono: user.telefono || "",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, nav]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/me/profile", profileForm);
      await refresh();
      toast.success("Profilo aggiornato.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il salvataggio.");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Le password non coincidono.");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/me/change-password", {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
      });
      setPwdForm({ current: "", next: "", confirm: "" });
      toast.success("Password aggiornata.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel cambio password.");
    } finally {
      setSavingPwd(false);
    }
  };

  if (authLoading || loading || !user) {
    return (
      <div className="section-dark min-h-screen flex items-center justify-center text-white/50 font-mono text-sm">
        Caricamento…
      </div>
    );
  }

  return (
    <div className="section-dark min-h-screen">
      <Toaster theme="dark" position="bottom-right" />

      {/* Nav */}
      <nav className="px-6 lg:px-10 py-5 border-b border-white/10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={22} />
          <span className="font-mono font-semibold text-[11px] tracking-[0.4em] text-white">
            L·I·F
          </span>
          <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--lif-accent)] ml-3">
            / Area personale
          </span>
        </Link>
        <div className="flex items-center gap-5 font-mono text-[11px] tracking-[0.2em] uppercase text-white/60">
          <span>{user?.nome} {user?.cognome}</span>
          <button
            onClick={() => {
              logout();
              nav("/");
            }}
            className="hover:text-[color:var(--lif-accent)] transition-colors"
          >
            Esci
          </button>
        </div>
      </nav>

      <div className="px-6 lg:px-10 py-10 max-w-[1200px] mx-auto">
        {/* Header */}
        <p className="eyebrow">Benvenuto</p>
        <h1
          className="mt-3 font-display font-bold text-white leading-none"
          style={{ fontSize: "clamp(36px, 5vw, 64px)" }}
        >
          {user.nome}<span className="text-[color:var(--lif-accent)]">.</span>
        </h1>

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-6 border-b border-white/10">
          <button
            onClick={() => setTab("dashboard")}
            data-testid="tab-dashboard"
            className={`py-3 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors ${
              tab === "dashboard"
                ? "text-white border-b-2 border-[color:var(--lif-accent)]"
                : "text-white/40 hover:text-white"
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setTab("profile")}
            data-testid="tab-profile"
            className={`py-3 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors ${
              tab === "profile"
                ? "text-white border-b-2 border-[color:var(--lif-accent)]"
                : "text-white/40 hover:text-white"
            }`}
          >
            Profilo
          </button>
        </div>

        {tab === "dashboard" && (
        <>
        {/* Progress — LIF to 100 */}
        <div className="mt-10 border border-white/10 rounded-lg p-6 bg-white/[0.02]">
          <div className="flex items-center justify-between mb-3">
            <p className="eyebrow">Avanzamento LIF</p>
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/60">
              {progress?.current ?? 0} / {progress?.target ?? 100} iscritti
            </p>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[color:var(--lif-accent)] transition-all duration-700"
              style={{ width: `${progress?.pct ?? 0}%` }}
            />
          </div>
          <p className="mt-3 font-body text-white/60 text-sm">
            Siamo{" "}
            <strong className="text-white">
              {progress?.pct ?? 0}% dell'obiettivo
            </strong>
            . Ogni invito avvicina il lancio.
          </p>
        </div>

        {/* Stats grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
            <p className="eyebrow">La tua rete</p>
            <p className="mt-2 font-display font-bold text-white text-3xl">
              {network?.total_descendants ?? 0}
            </p>
            <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-white/50 mt-1">
              persone in 5 livelli
            </p>
          </div>
          <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
            <p className="eyebrow">Guadagni corso</p>
            <p className="mt-2 font-display font-bold text-white text-3xl">
              €{centsToEuro(earnings?.course_earned_cents)}
            </p>
          </div>
          <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
            <p className="eyebrow">Guadagni altre attività</p>
            <p className="mt-2 font-display font-bold text-white text-3xl">
              €{centsToEuro(earnings?.activity_earned_cents)}
            </p>
          </div>
        </div>

        {/* Referral code + share */}
        <div className="mt-6 border border-white/10 rounded-lg p-6 sm:p-8 bg-white/[0.02]">
          <p className="eyebrow">Il tuo codice invito</p>
          <p
            className="mt-3 font-mono font-bold text-[color:var(--lif-accent)] tracking-[0.24em]"
            style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
          >
            {user.referral_code}
          </p>
          <div className="mt-6 bg-white rounded-lg p-5 text-[color:var(--lif-light-fg)]">
            <SharePanel code={user.referral_code} nome={user.nome} />
          </div>
        </div>

        {/* Network tree */}
        <div className="mt-6 border border-white/10 rounded-lg p-6 bg-white/[0.02]">
          <h2 className="font-display font-bold text-white text-xl mb-5">
            La tua rete · 5 livelli
          </h2>
          {network?.levels?.length ? (
            <div className="space-y-5">
              {network.levels.map((lvl) => (
                <div key={lvl.level}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[color:var(--lif-accent)]">
                      Livello {lvl.level}
                    </p>
                    <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-white/50">
                      {lvl.count} {lvl.count === 1 ? "persona" : "persone"}
                    </p>
                  </div>
                  {lvl.count === 0 ? (
                    <p className="text-white/30 text-sm font-body">
                      — nessun invito a questo livello —
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {lvl.members.map((m) => (
                        <span
                          key={m.referral_code}
                          className="inline-flex items-center gap-2 border border-white/15 rounded-md px-3 py-1.5 text-sm text-white"
                        >
                          <span className="font-mono text-[10px] text-[color:var(--lif-accent)] tracking-[0.12em]">
                            {m.referral_code}
                          </span>
                          <span className="font-body">
                            {m.nome} {m.cognome || ""}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/50 text-sm font-body">
              La tua rete è vuota. Condividi il tuo link per iniziare.
            </p>
          )}
        </div>
        </>
        )}

        {tab === "profile" && (
          <div className="mt-10 space-y-6">
            {/* Account info */}
            <div className="border border-white/10 rounded-lg p-6 bg-white/[0.02]">
              <p className="eyebrow">Account</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-body">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                    Email
                  </p>
                  <p className="mt-1 text-white">{user.email}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                    Codice referral
                  </p>
                  <p className="mt-1 font-mono text-[color:var(--lif-accent)] tracking-[0.14em]">
                    {user.referral_code}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                    Invitato da
                  </p>
                  <p className="mt-1 text-white">{user.referred_by || "—"}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                    Iscritto il
                  </p>
                  <p className="mt-1 text-white">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString("it-IT")
                      : "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Edit profile */}
            <form
              onSubmit={saveProfile}
              className="border border-white/10 rounded-lg p-6 bg-white/[0.02] space-y-4"
            >
              <p className="eyebrow">Dati personali</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                    Nome
                  </span>
                  <input
                    type="text"
                    value={profileForm.nome}
                    onChange={(e) =>
                      setProfileForm({ ...profileForm, nome: e.target.value })
                    }
                    data-testid="profile-nome-input"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                    Cognome
                  </span>
                  <input
                    type="text"
                    value={profileForm.cognome}
                    onChange={(e) =>
                      setProfileForm({ ...profileForm, cognome: e.target.value })
                    }
                    data-testid="profile-cognome-input"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                    Telefono
                  </span>
                  <input
                    type="tel"
                    value={profileForm.telefono}
                    onChange={(e) =>
                      setProfileForm({ ...profileForm, telefono: e.target.value })
                    }
                    data-testid="profile-telefono-input"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={savingProfile}
                data-testid="profile-save-btn"
                className="lif-btn py-3 px-6"
              >
                {savingProfile ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </form>

            {/* Change password */}
            <form
              onSubmit={changePassword}
              className="border border-white/10 rounded-lg p-6 bg-white/[0.02] space-y-4"
            >
              <p className="eyebrow">Cambia password</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                    Password attuale
                  </span>
                  <input
                    type="password"
                    required
                    value={pwdForm.current}
                    onChange={(e) =>
                      setPwdForm({ ...pwdForm, current: e.target.value })
                    }
                    data-testid="pwd-current-input"
                    autoComplete="current-password"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                    Nuova password
                  </span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pwdForm.next}
                    onChange={(e) =>
                      setPwdForm({ ...pwdForm, next: e.target.value })
                    }
                    data-testid="pwd-new-input"
                    autoComplete="new-password"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                    Conferma
                  </span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pwdForm.confirm}
                    onChange={(e) =>
                      setPwdForm({ ...pwdForm, confirm: e.target.value })
                    }
                    data-testid="pwd-confirm-input"
                    autoComplete="new-password"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={savingPwd}
                data-testid="pwd-save-btn"
                className="lif-btn py-3 px-6"
              >
                {savingPwd ? "Salvataggio..." : "Aggiorna password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAreaPage;
